import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import {
  Target, CheckCircle, ArrowRight, Sparkles,
  BookOpen, Clock, DollarSign, Lightbulb,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { getCanonicalUrl, getDefaultOgImage } from '@/lib/seoUtils';

const steps = [
  {
    icon: BookOpen,
    title: "Choose Your Subjects",
    desc: "Select the subjects or skills you want to learn — from IIT-JEE prep to music, coding, languages, and more. You can always add more later.",
  },
  {
    icon: Clock,
    title: "Set Your Availability",
    desc: "Tell us your preferred days and time slots. This helps narrow down teachers who are actually available when you need them.",
  },
  {
    icon: DollarSign,
    title: "Define Your Budget",
    desc: "Set a comfortable price range. We'll surface teachers whose session rates fit what you're willing to spend — no surprises.",
  },
  {
    icon: Lightbulb,
    title: "Describe Your Learning Style",
    desc: "Do you prefer structured lessons, hands-on practice, or open discussions? Sharing this helps teachers tailor their approach to you.",
  },
];

export default function HowItWorksGoalsPage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const title = 'Set Your Learning Goals | BookMyCoaching';
  const description = 'Tell us your subject, schedule, and budget — BookMyCoaching finds teachers who match exactly what you need.';
  const canonicalUrl = getCanonicalUrl(pathname);
  const ogImage = getDefaultOgImage();

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
      </Helmet>
      <Header />

      <main>

        {/* ── Hero ── */}
        <section className="relative pt-28 pb-20 sm:pt-44 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[hsl(237,52%,92%)]/80 via-[hsl(237,45%,90%)]/60 to-[hsl(39,96%,93%)]/50" />
          <svg className="absolute inset-0 w-full h-full opacity-[0.03] pointer-events-none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="goals-grid" x="0" y="0" width="44" height="44" patternUnits="userSpaceOnUse">
                <path d="M 44 0 L 0 0 0 44" fill="none" stroke="hsl(237,52%,31%)" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#goals-grid)" />
          </svg>
          <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-white/40 rounded-full blur-[100px] pointer-events-none" />

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/80 border border-[hsl(237,52%,31%)]/20 shadow-sm mb-6">
                <Sparkles className="w-3.5 h-3.5 text-[hsl(39,96%,48%)]" />
                <span className="text-xs font-semibold text-[#262A7A] tracking-wide">Step 1 · For Students</span>
              </div>

              <h1 className="font-sans text-4xl sm:text-5xl lg:text-[3.25rem] font-extrabold text-[#262A7A] leading-[1.1] tracking-tight mb-5">
                Start with what you<br />
                <span className="relative inline-block text-[hsl(237,52%,31%)]">
                  want to achieve.
                  <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 340 12" fill="none" aria-hidden="true">
                    <path d="M4 9 C 85 3, 255 3, 336 9" stroke="hsl(39,96%,48%)" strokeWidth="4" strokeLinecap="round" />
                  </svg>
                </span>
              </h1>

              <p className="text-[#5A5E80] text-lg sm:text-xl leading-relaxed max-w-2xl mb-10">
                Setting clear goals is how we connect you to the right teacher. Tell us your subject, availability, and learning style — we handle the rest.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  className="rounded-full px-8 gap-2 bg-[hsl(237,52%,31%)] hover:bg-[hsl(237,52%,25%)] text-white shadow-xl shadow-[hsl(237,52%,31%)]/25 hover:-translate-y-0.5 transition-all"
                  onClick={() => navigate('/search')}
                >
                  Find a Teacher
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <Button size="lg" variant="outline" className="rounded-full px-8" asChild>
                  <Link to="/how-it-works/matching">How matching works →</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* ── What to set up ── */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center mb-14">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-[hsl(237,52%,50%)] mb-3 px-3 py-1 rounded-full bg-[hsl(237,52%,31%)]/10">
                Four things to define
              </span>
              <h2 className="font-sans text-3xl sm:text-4xl text-[#262A7A] mb-3">What helps us find your best match</h2>
              <p className="text-[#5A5E80] text-sm max-w-md mx-auto">The more specific you are, the better teachers we can surface for you.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {steps.map((step, i) => (
                <div key={i} className="flex gap-4 p-6 rounded-2xl border border-[#DCDDEE] bg-[#F7F7FC]/60 hover:shadow-md hover:-translate-y-0.5 hover:border-[#DCDDEE] transition-all duration-200">
                  <div className="w-10 h-10 rounded-xl bg-[hsl(237,52%,31%)]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <step.icon className="w-5 h-5 text-[hsl(237,52%,40%)]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#262A7A] text-base mb-1.5">{step.title}</h3>
                    <p className="text-sm text-[#5A5E80] leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Tip card ── */}
        <section className="py-16 bg-[#F7F7FC]">
          <div className="container mx-auto px-4 max-w-3xl">
            <div className="rounded-3xl border border-[hsl(237,52%,31%)]/15 bg-[hsl(237,52%,31%)]/5 p-8 sm:p-10 flex gap-5 items-start">
              <div className="w-10 h-10 rounded-xl bg-[hsl(237,52%,31%)]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                <CheckCircle className="w-5 h-5 text-[hsl(237,52%,40%)]" />
              </div>
              <div>
                <h3 className="font-semibold text-[#262A7A] text-base mb-2">No account needed to browse</h3>
                <p className="text-sm text-[#5A5E80] leading-relaxed">
                  You can search and explore teacher profiles without signing up. Create an account only when you're ready to book a session or contact a teacher directly.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[hsl(237,52%,25%)] via-[hsl(237,52%,31%)] to-[hsl(237,52%,20%)]" />
          <div className="absolute -top-20 -right-20 w-[400px] h-[400px] bg-white/5 rounded-full blur-[100px] pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-[hsl(39,96%,48%)]/10 rounded-full blur-[100px] pointer-events-none" />

          <div className="container mx-auto px-4 relative z-10 text-center">
            <div className="max-w-xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white/90 text-sm font-medium mb-8 backdrop-blur-md">
                <Target className="w-3.5 h-3.5 text-[hsl(39,96%,58%)]" />
                Start with one subject — grow from there
              </div>
              <h2 className="font-sans text-4xl sm:text-5xl text-white mb-5 leading-tight">
                Ready to find your teacher?
              </h2>
              <p className="text-indigo-200 text-lg mb-10 leading-relaxed">
                Browse verified teachers by subject, level, and price — no commitment required.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  size="lg"
                  className="rounded-full px-10 py-6 bg-white text-[hsl(237,52%,31%)] hover:bg-white/90 shadow-xl shadow-black/20 hover:-translate-y-0.5 transition-all font-semibold"
                  onClick={() => navigate('/search')}
                >
                  Browse Teachers
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button size="lg" variant="outline" className="rounded-full px-10 py-6 border-white/30 text-white hover:bg-white/10" asChild>
                  <Link to="/how-it-works">Learn more first</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
